/**
 * Bloqueo de una versión por su estado (OT-0064).
 *
 * El backend es la única fuente de la regla "esta versión se puede editar":
 * la expone como `ArtifactVersion.is_editable` y el frontend no la recalcula.
 * Cuando una escritura llega con la regla ya cambiada —el documento entró a
 * revisión, o la versión se aprobó o se descartó mientras el editor estaba
 * abierto— el servidor la rechaza con 409 y uno de estos códigos.
 *
 * `markVersionLocked` aplica ese veredicto a la caché de la versión
 * (`is_editable: false`): las vistas que derivan su render de esa query se
 * bloquean en el mismo render, y la invalidación trae el estado real.
 */

import type { QueryClient } from '@tanstack/react-query'

import type { ArtifactVersion } from '../api/artifacts'
import { ApiError } from '../api/client'

const VERSION_LOCK_CODES = ['ARTIFACT_IN_REVIEW', 'ARTIFACT_VERSION_NOT_DRAFT'] as const

export type VersionLockCode = (typeof VERSION_LOCK_CODES)[number]

/** Para los rechazos que no llegan como `ApiError`, p.ej. el evento `error`
 * del stream de «Generar todo». */
export function isVersionLockCode(code: string | undefined): code is VersionLockCode {
  return (VERSION_LOCK_CODES as readonly (string | undefined)[]).includes(code)
}

export function isVersionLockError(err: unknown): err is ApiError {
  return err instanceof ApiError && isVersionLockCode(err.code)
}

export function markVersionLocked(
  queryClient: QueryClient,
  artifactId: string,
  versionId: string | null,
): void {
  if (versionId) {
    queryClient.setQueryData<ArtifactVersion>(
      ['artifacts', artifactId, 'versions', versionId],
      (old) => (old ? { ...old, is_editable: false } : old),
    )
  }
  void queryClient.invalidateQueries({ queryKey: ['artifacts', artifactId] })
}
